// Phone camera photos can be 10-40MB depending on device/resolution, which blows past
// upload limits regardless of how high we set them. Resizing/re-encoding on the client
// keeps uploads small and consistent no matter what camera or resolution was used.
const MAX_DIMENSION_PX = 2000;
const JPEG_QUALITY = 0.85;
const SKIP_COMPRESSION_BELOW_BYTES = 1.5 * 1024 * 1024;

export async function compressImageForUpload(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    return file;
  }
  if (file.size <= SKIP_COMPRESSION_BELOW_BYTES) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(
      file,
      { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions
    );
    const scale = Math.min(1, MAX_DIMENSION_PX / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    );
    if (!blob || blob.size >= file.size) {
      return file;
    }

    const baseName = file.name.replace(/\.[^./\\]+$/, '') || 'upload';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}
